// src/stores/useMidiDevicesStore.ts
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type { MidiDevice, MidiCC } from '~/types';
import { getSynthInitialCCValues } from '~/lib/audio/ccMapping';
import { applyProfileToCCs } from '~/lib/profiles/applyProfile';
import { VIRTUAL_SYNTHS } from '~/config/devices';
import type { VirtualSynthConfig } from '~/config/devices';

/**
 * Get initial CC values for a virtual synth
 * Initializes all 128 CCs with supported ones visible
 */
export const getInitialCCs = (config: VirtualSynthConfig): MidiCC[] => {
  const supportedCCs = new Set(config.supportedCCs);

  // Start with all 128 CCs at 0
  const ccs = Array.from({ length: 128 }, (_, i) => ({
    number: i,
    value: 0,
    scramble: false,
    smooth: false,
    show: supportedCCs.has(i), // Only show supported CCs by default
    min: 0,
    max: 127,
  }));

  // Set initial values for mapped CCs
  const mappedCCs = getSynthInitialCCValues();
  mappedCCs.forEach(({ number, value }) => {
    if (ccs[number]) {
      ccs[number].value = value;
    }
  });

  return ccs;
};

/**
 * Create a MidiDevice from a VirtualSynthConfig
 */
function createVirtualDevice(config: VirtualSynthConfig): MidiDevice {
  const initialCCs = getInitialCCs(config);
  const profileAppliedCCs = applyProfileToCCs(initialCCs, config.profile);

  return {
    label: config.label,
    cc: profileAppliedCCs,
    profiles: [config.profile],
    activeProfileId: config.profile.id,
    state: "connected",
    enabled: true,
    synthType: config.synthType, // Add synthType for Tone.js synth creation
  };
}

interface MidiDevicesState {
  devices: MidiDevice[];
  setDevices: (devices: MidiDevice[] | ((prev: MidiDevice[]) => MidiDevice[])) => void;
  updateDevice: (index: number, updates: Partial<MidiDevice>) => void;
}

// Initialize all virtual synths
const initialDevices = VIRTUAL_SYNTHS.map(createVirtualDevice);

export const useMidiDevicesStore = create<MidiDevicesState>()(
  immer((set) => ({
    devices: initialDevices,

    setDevices: (devicesOrFn) =>
      set((state) => {
        state.devices = typeof devicesOrFn === 'function' ? devicesOrFn(state.devices) : devicesOrFn;
      }),

    updateDevice: (index, updates) =>
      set((state) => {
        if (state.devices[index]) {
          Object.assign(state.devices[index], updates);
        }
      }),
  }))
);
